import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Bus, Search, ExternalLink } from "lucide-react";

export default function Vehicles() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ code: "", plate: "", brand: "", route: "", model: "", year: "", active: true, next_service_date: "", next_inspection_date: "", last_service_km: "", notes: "" });

  const load = async () => setItems((await api.get("/vehicles")).data);
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ code: "", plate: "", brand: "", route: "", model: "", year: "", active: true, next_service_date: "", next_inspection_date: "", last_service_km: "", notes: "" }); setOpen(true); };
  const openEdit = (v) => { setEditing(v); setForm({ ...v, brand: v.brand || "", year: v.year || "", last_service_km: v.last_service_km || "", next_service_date: v.next_service_date || "", next_inspection_date: v.next_inspection_date || "", notes: v.notes || "" }); setOpen(true); };

  const save = async () => {
    if (!form.code.trim() || !form.plate.trim()) return toast.error("Kod ve plaka zorunlu");
    try {
      const payload = {
        ...form,
        year: form.year ? parseInt(form.year) : null,
        last_service_km: form.last_service_km ? parseInt(form.last_service_km) : null,
        next_service_date: form.next_service_date || null,
        next_inspection_date: form.next_inspection_date || null,
      };
      if (editing) await api.put(`/vehicles/${editing.id}`, payload);
      else await api.post("/vehicles", payload);
      toast.success(editing ? "Güncellendi" : "Eklendi");
      setOpen(false); load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Hata");
    }
  };

  const del = async (id) => {
    if (!confirm("Silmek istediğinize emin misiniz?")) return;
    await api.delete(`/vehicles/${id}`);
    toast.success("Silindi"); load();
  };

  const filtered = items.filter((v) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return v.code.toLowerCase().includes(s) || v.plate.toLowerCase().includes(s) || (v.route || "").toLowerCase().includes(s) || (v.brand || "").toLowerCase().includes(s);
  });

  return (
    <div className="space-y-6" data-testid="vehicles-page">
      <div className="flex items-baseline justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-slate-500 font-mono">Filo Yönetimi</div>
          <h1 className="mt-1 font-heading font-bold text-3xl lg:text-4xl tracking-tight text-slate-900">Araçlar & Otobüsler</h1>
          <p className="mt-2 text-sm text-slate-500">YAZTUR filosundaki tüm araçların kod, plaka ve hat bilgileri.</p>
        </div>
        <Button onClick={openNew} data-testid="add-vehicle-btn" className="bg-[#1E3A8A] hover:bg-[#1e40af]">
          <Plus className="w-4 h-4 mr-2" /> Yeni Araç
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <MiniStat label="Toplam Araç" value={items.length} />
        <MiniStat label="Aktif" value={items.filter((i) => i.active).length} color="text-emerald-600" />
        <MiniStat label="Pasif" value={items.filter((i) => !i.active).length} color="text-slate-400" />
      </div>

      <Card className="border-slate-200 shadow-none">
        <CardHeader className="border-b border-slate-100">
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading text-lg">Filo Listesi</CardTitle>
            <div className="relative w-64">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input placeholder="Kod, plaka, marka veya hat ara..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="vehicle-search" />
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-6">
          <div className="overflow-x-auto rounded-md border border-slate-200">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  <TableHead className="text-xs uppercase tracking-wider">Kod</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Plaka</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Marka</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Hat / Rota</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Sonraki Servis</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Muayene</TableHead>
                  <TableHead className="text-xs uppercase tracking-wider">Durum</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((v) => {
                  const daysToSvc = v.next_service_date ? Math.ceil((new Date(v.next_service_date) - new Date()) / (1000 * 60 * 60 * 24)) : null;
                  const daysToInsp = v.next_inspection_date ? Math.ceil((new Date(v.next_inspection_date) - new Date()) / (1000 * 60 * 60 * 24)) : null;
                  return (
                  <TableRow key={v.id} className="hover:bg-slate-50" data-testid={`vehicle-${v.code}`}>
                    <TableCell className="font-mono font-bold text-[#1E3A8A]">
                      <Link to={`/arac/${v.code}`} className="hover:underline flex items-center gap-1">
                        {v.code} <ExternalLink className="w-3 h-3 opacity-60" />
                      </Link>
                    </TableCell>
                    <TableCell className="font-semibold">
                      <Link to={`/arac/${v.code}`} className="flex items-center gap-2 hover:text-[#1E3A8A]">
                        <div className="w-8 h-8 rounded-md bg-blue-50 text-[#1E3A8A] flex items-center justify-center">
                          <Bus className="w-4 h-4" />
                        </div>
                        {v.plate}
                      </Link>
                    </TableCell>
                    <TableCell data-testid={`vehicle-brand-${v.code}`}>{v.brand || "-"}</TableCell>
                    <TableCell>{v.route || "-"}</TableCell>
                    <TableCell>
                      {v.next_service_date ? (
                        <span className={`text-xs px-2 py-1 rounded font-mono ${daysToSvc <= 15 ? "bg-amber-50 text-amber-800 border border-amber-200" : "bg-slate-50 text-slate-700 border border-slate-200"}`}>
                          {new Date(v.next_service_date).toLocaleDateString("tr-TR")} ({daysToSvc >= 0 ? `${daysToSvc}g` : `${-daysToSvc}g geç`})
                        </span>
                      ) : "-"}
                    </TableCell>
                    <TableCell>
                      {v.next_inspection_date ? (
                        <span className={`text-xs px-2 py-1 rounded font-mono ${daysToInsp <= 15 ? "bg-amber-50 text-amber-800 border border-amber-200" : "bg-slate-50 text-slate-700 border border-slate-200"}`}>
                          {new Date(v.next_inspection_date).toLocaleDateString("tr-TR")} ({daysToInsp >= 0 ? `${daysToInsp}g` : `${-daysToInsp}g geç`})
                        </span>
                      ) : "-"}
                    </TableCell>
                    <TableCell>
                      {v.active
                        ? <span className="text-xs px-2 py-1 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">Aktif</span>
                        : <span className="text-xs px-2 py-1 rounded bg-slate-100 text-slate-500 border border-slate-200">Pasif</span>}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" onClick={() => openEdit(v)}><Pencil className="w-4 h-4" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => del(v.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                    </TableCell>
                  </TableRow>
                  );
                })}
                {filtered.length === 0 && (
                  <TableRow><TableCell colSpan={8} className="text-center py-8 text-slate-400">Sonuç bulunamadı</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Aracı Düzenle" : "Yeni Araç"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 max-h-[70vh] overflow-y-auto pr-1">
            <div>
              <Label className="text-xs uppercase tracking-wider">Otobüs Kodu *</Label>
              <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} className="mt-1 font-mono" placeholder="481" data-testid="vehicle-code-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Plaka *</Label>
              <Input value={form.plate} onChange={(e) => setForm({ ...form, plate: e.target.value.toUpperCase() })} className="mt-1 font-mono" placeholder="16 BGY 277" data-testid="vehicle-plate-input" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wider">Marka</Label>
              <Input value={form.brand || ""} onChange={(e) => setForm({ ...form, brand: e.target.value })} className="mt-1" placeholder="Örn: Mercedes" data-testid="vehicle-brand-input" />
            </div>
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wider">Bağlı Olduğu Hat</Label>
              <Input value={form.route} onChange={(e) => setForm({ ...form, route: e.target.value })} className="mt-1" placeholder="Ör: Merkez-Şehirlerarası" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Model</Label>
              <Input value={form.model} onChange={(e) => setForm({ ...form, model: e.target.value })} className="mt-1" placeholder="Örn: Mercedes Travego" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Yıl</Label>
              <Input type="number" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Sonraki Servis Tarihi</Label>
              <Input type="date" value={form.next_service_date || ""} onChange={(e) => setForm({ ...form, next_service_date: e.target.value })} className="mt-1" data-testid="next-service-input" />
              <p className="text-[10px] text-slate-500 mt-1">30 gün öncesi bildirim tetiklenir</p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Muayene Tarihi</Label>
              <Input type="date" value={form.next_inspection_date || ""} onChange={(e) => setForm({ ...form, next_inspection_date: e.target.value })} className="mt-1" data-testid="next-inspection-input" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Son Servis KM (opsiyonel)</Label>
              <Input type="number" value={form.last_service_km} onChange={(e) => setForm({ ...form, last_service_km: e.target.value })} className="mt-1" />
            </div>
            <div className="flex items-center justify-between border border-slate-200 rounded-md p-3">
              <span className="text-sm">Aktif</span>
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
            </div>
            <div className="col-span-2">
              <Label className="text-xs uppercase tracking-wider">Notlar</Label>
              <Input value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1" placeholder="Serbest not..." />
            </div>
            <Button onClick={save} data-testid="save-vehicle-btn" className="col-span-2 bg-[#1E3A8A] hover:bg-[#1e40af]">
              {editing ? "Güncelle" : "Kaydet"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MiniStat({ label, value, color = "text-slate-900" }) {
  return (
    <div className="bg-white border border-slate-200 rounded-md p-4">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono">{label}</div>
      <div className={`metric-num text-2xl font-bold mt-1 ${color}`}>{value}</div>
    </div>
  );
}
